import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildFileUrl } from '../../apiConfig';
import { BackNavLink } from '../../components/NavButtons';
import { getStudentClassContentById } from '../../services/classContentService';

const contentTypeConfig = {
  pdf: {
    label: 'فایل PDF',
    background: 'linear-gradient(90deg, #EB8181 0%, #E02727 100%)',
  },
  image: {
    label: 'تصویر',
    background: 'linear-gradient(90deg, #B9ABFF 0%, #896CFF 100%)',
  },
  video: {
    label: 'ویدیو',
    background: 'linear-gradient(90deg, #FFB55A 0%, #FF9238 100%)',
  },
  link: {
    label: 'لینک',
    background: 'linear-gradient(90deg, #ABCEF5 0%, #67A8EF 100%)',
  },
};

function getContentTypeConfig(contentType) {
  return contentTypeConfig[contentType] || {
    label: 'محتوا',
    background: 'linear-gradient(90deg, #00C0B9 0%, #54B0F8 100%)',
  };
}

function getContentFileUrl(content) {
  return buildFileUrl(content?.file?.url || content?.file?.fileUrl || content?.file?.path || '');
}

function ContentViewer({ content }) {
  const fileUrl = getContentFileUrl(content);

  if (content.contentType === 'link') {
    const linkUrl = content.linkUrl || '';

    return (
      <div className="space-y-3">
        <div className="rounded-[10px] bg-[#F8F8F8] px-4 py-3">
          <p className="text-[11px] text-slate-500">آدرس لینک</p>
          <p className="mt-1 break-all text-[12px] font-semibold text-[#67A8EF]">{linkUrl || 'لینکی ثبت نشده است.'}</p>
        </div>

        {linkUrl && (
          <a
            href={linkUrl}
            target="_blank"
            rel="noreferrer"
            className="flex w-full items-center justify-center rounded-[6px] bg-[#67A8EF] px-4 py-2 text-[13px] font-bold text-white"
          >
            باز کردن لینک
          </a>
        )}
      </div>
    );
  }

  if (content.contentType === 'image' && fileUrl) {
    return (
      <div className="space-y-3">
        <div className="overflow-hidden rounded-[10px] bg-[#F8F8F8]">
          <img
            src={fileUrl}
            alt={content.title || 'تصویر محتوا'}
            className="mx-auto max-h-[420px] w-full object-contain"
          />
        </div>

        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="flex w-full items-center justify-center rounded-[6px] bg-[#896CFF] px-4 py-2 text-[13px] font-bold text-white"
        >
          باز کردن تصویر در تب جدید
        </a>
      </div>
    );
  }

  if (content.contentType === 'video' && fileUrl) {
    return (
      <div className="space-y-3">
        <div className="overflow-hidden rounded-[10px] bg-black">
          <video
            src={fileUrl}
            controls
            playsInline
            className="mx-auto max-h-[420px] w-full"
          >
            مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
          </video>
        </div>

        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="flex w-full items-center justify-center rounded-[6px] bg-[#FF9238] px-4 py-2 text-[13px] font-bold text-white"
        >
          دانلود ویدیو
        </a>
      </div>
    );
  }

  if (content.contentType === 'pdf' && fileUrl) {
    return (
      <div className="space-y-3">
        <div className="overflow-hidden rounded-[10px] border border-slate-200 bg-white">
          <iframe
            title={content.title || 'فایل PDF'}
            src={fileUrl}
            className="h-[420px] w-full"
          />
        </div>

        <a
          href={fileUrl}
          target="_blank"
          rel="noreferrer"
          className="flex w-full items-center justify-center rounded-[6px] bg-[#E02727] px-4 py-2 text-[13px] font-bold text-white"
        >
          باز کردن PDF در تب جدید
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-[8px] bg-[#F8F8F8] px-4 py-5 text-center">
      <p className="text-[13px] font-bold text-slate-800">محتوایی برای نمایش وجود ندارد</p>
      <p className="mt-2 text-[11px] text-slate-500">فایل یا لینک این محتوا در دسترس نیست.</p>
    </div>
  );
}

function ContentDetailsPage() {
  const { contentId } = useParams();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadContent() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const contentData = await getStudentClassContentById(contentId);

        if (isMounted) {
          setContent(contentData);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت محتوا وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadContent();

    return () => {
      isMounted = false;
    };
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/contents">بازگشت به محتواها</BackNavLink>
        <div className="rounded-[14px] bg-white p-6 text-center text-[12px] text-slate-500 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          در حال دریافت محتوا...
        </div>
      </div>
    );
  }

  if (errorMessage && !content) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/contents">بازگشت به محتواها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[13px] font-bold text-[#E02727]">{errorMessage}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 rounded-[6px] bg-[#00C0B9] px-5 py-1 text-[12px] font-bold text-white"
          >
            تلاش دوباره
          </button>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/contents">بازگشت به محتواها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[15px] font-bold text-slate-800">محتوا پیدا نشد</p>
          <p className="mt-2 text-[12px] text-slate-500">این محتوا در دسترس نیست.</p>
        </div>
      </div>
    );
  }

  const typeConfig = getContentTypeConfig(content.contentType);

  return (
    <div className="space-y-5">
      <BackNavLink to="/contents">بازگشت به محتواها</BackNavLink>

      <div className="flex items-center gap-2">
        <p
          style={{ background: typeConfig.background, boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
          className="px-6 py-1 rounded-[10px] text-[15px] text-white"
        >
          {content.title || 'محتوا'}
        </p>

        <img className="h-[50px]" src="/images/content.webp" alt="" />
      </div>

      <div>
        <p className="text-[11px]">جزئیات محتوای آموزشی</p>
        <p className="text-[11px] font-light">
          اطلاعات کامل محتوا و فایل یا لینک مربوطه را در ادامه ببین.
        </p>
      </div>

      {content.description?.trim() ? (
        <div className="rounded-[10px] bg-white p-4 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] font-bold text-slate-800">{content.description}</p>
            <span
              className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold text-white"
              style={{ background: typeConfig.background }}
            >
              {typeConfig.label}
            </span>
          </div>
        </div>
      ) : null}

      <div
        className="rounded-[10px] p-3"
        style={{ background: 'rgba(217, 217, 217, 0.3)' }}
      >
        <p className="mb-3 text-[12px] font-bold text-slate-800">نمایش محتوا</p>
        <ContentViewer content={content} />
      </div>
    </div>
  );
}

export default ContentDetailsPage;
